import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { motion } from 'framer-motion';
import { Sparkles, Clock, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://struct-code-production.up.railway.app';

const PageContainer = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 20px 12px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: clamp(2rem, 5vw, 2.8rem);
  color: ${props => props.theme.colors.text};
  margin: 0 0 12px;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const Card = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  backdrop-filter: blur(20px);
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: ${props => props.theme.shadows.lg};
  cursor: pointer;
  text-decoration: none;
  display: block;
  color: inherit;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  background: ${props => props.theme.colors.bgPrimary};
  color: ${props => props.theme.colors.starGold};
  border: 1px solid ${props => props.theme.colors.lineGold};
  border-radius: ${props => props.theme.borderRadius.full};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  font-weight: 500;
`;

const TypeCode = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  letter-spacing: 1px;
`;

const TypeName = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 4px;
`;

const DateText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textLight};
  margin-top: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.theme.colors.textSecondary};
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 16px;
  text-align: center;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
`;

const LoginPrompt = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const LoginButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 20px;
  font-family: ${props => props.theme.fonts.main};
`;

const MyDiagnosesPage = () => {
  const { isAuthenticated, token, login } = useAuth();
  const { t } = useLanguage();
  const theme = useTheme();
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && token) {
      const fetchDiagnoses = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/v2/dynamic/my-diagnoses`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setDiagnoses(response.data);
        } catch (error) {
          console.error('Failed to fetch diagnoses:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchDiagnoses();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  if (!isAuthenticated) {
    return (
      <PageContainer initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Header>
          <Title>{t('マイ診断', 'My Diagnoses')}</Title>
        </Header>
        <LoginPrompt>
          <Sparkles size={48} color={theme.colors.textMuted} />
          <p style={{ color: theme.colors.textSecondary, marginTop: '16px', fontSize: '1.1rem' }}>
            {t('診断履歴を見るにはログインが必要です', 'Login required to view diagnosis history')}
          </p>
          <LoginButton onClick={login} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            {t('Googleでログイン', 'Login with Google')}
            <ArrowRight size={18} />
          </LoginButton>
        </LoginPrompt>
      </PageContainer>
    );
  }

  return (
    <PageContainer initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <Header>
        <Title>{t('マイ診断', 'My Diagnoses')}</Title>
        <Subtitle>{t('あなたの過去の診断結果', 'Your past diagnosis results')}</Subtitle>
      </Header>

      {loading ? (
        <EmptyState>{t('読み込み中...', 'Loading...')}</EmptyState>
      ) : diagnoses.length === 0 ? (
        <EmptyState>
          <Sparkles size={48} style={{ opacity: 0.5 }} />
          <p style={{ marginTop: '16px' }}>{t('まだ診断結果がありません', 'No diagnosis results yet')}</p>
          <Link to="/current-diagnosis" style={{ color: theme.colors.text, marginTop: '12px', display: 'inline-block' }}>
            {t('診断を始める', 'Start Diagnosis')} →
          </Link>
        </EmptyState>
      ) : (
        <>
        <StatsRow>
          <StatCard>
            <StatLabel>{t('診断数', 'Total')}</StatLabel>
            <StatValue>{diagnoses.length}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>{t('最新タイプ', 'Latest Type')}</StatLabel>
            <StatValue>{diagnoses[0]?.current_type_code || diagnoses[0]?.natal_type_code || '—'}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>{t('最終診断日', 'Last Date')}</StatLabel>
            <StatValue>{diagnoses[0]?.created_at ? new Date(diagnoses[0].created_at).toLocaleDateString() : '—'}</StatValue>
          </StatCard>
        </StatsRow>
        {diagnoses.map((d, index) => (
          <Card
            key={d.diagnosis_id || index}
            as={d.diagnosis_id ? Link : 'div'}
            to={d.diagnosis_id ? `/current-result/${d.diagnosis_id}` : '#'}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <CardHeader>
              <TypeBadge>
                {d.diagnosis_type === 'natal' ? t('本来の構造', 'Innate') : t('現在の構造', 'Current')}
              </TypeBadge>
              <ArrowRight size={18} color="#9CA3AF" />
            </CardHeader>
            <TypeCode>{d.current_type_code || d.natal_type_code}</TypeCode>
            <TypeName>{d.current_type_label || d.natal_type_label}</TypeName>
            {d.created_at && (
              <DateText>
                <Clock size={14} />
                {new Date(d.created_at).toLocaleDateString()}
              </DateText>
            )}
          </Card>
        ))}
        </>
      )}
    </PageContainer>
  );
};

export default MyDiagnosesPage;
